import {
  factorSliderUsesDecimalUi,
  scalarSliderToApi,
  type ContributingFactorUiKind,
} from './contributing-factors-form-patch';
import type { FeatureMetadataRow } from './feature-metadata.models';
import type { CaptainListItem } from './ship.models';
import type {
  VoyageContributingFactorRow,
  VoyageContributingFactorsApiData,
  VoyageContributingFactorsVoyage,
  VoyageContributingPerCaptainNamed,
  VoyageContributingPerCaptainScalar,
} from './voyage-contributing-factors.models';
import {
  CAPTAIN_CREW_RATIO_WEIGHTED_API_NAME,
  contributingFactorsCaptainRosterForSlots,
  contributingFactorsVoyageById,
} from './voyage-contributing-factors.models';

/** Mirrors `scalarSliderFromApi` in contributing-factors-form-patch (slider UI ↔ model scale). */
function formSliderToModelValue(
  ui: number,
  factorRow: VoyageContributingFactorRow,
  meta: FeatureMetadataRow,
): number {
  return scalarSliderToApi(ui, meta, factorRow.aggregateValue);
}

function findMutableFactorRow(
  voyage: VoyageContributingFactorsVoyage,
  featureId: number,
): VoyageContributingFactorRow | null {
  const b = voyage.factors;
  for (const bucket of [b.captain, b.crew, b.ship, b.voyage]) {
    const hit = bucket?.find((r) => r.featureId === featureId);
    if (hit) {
      return hit;
    }
  }
  return null;
}

function optionNameById(
  options: ReadonlyArray<{ id: unknown; name: unknown }>,
  id: string,
): string {
  const o = options.find((x) => String(x.id) === id);
  if (o == null) {
    return '';
  }
  const label = String(o.name ?? '').trim();
  return label.length > 0 ? label : String(o.id ?? '');
}

function resolveCaptainManningDropdownName(
  meta: FeatureMetadataRow,
  id: string,
  ctx: MergeFormIntoVoyageContext,
): string {
  const ui = meta.uiName.trim().toLowerCase();
  const api = meta.apiName.toLowerCase();
  const isCaptainLike = ui === 'captain' || api.includes('captaincrew');
  const isManningLike = ui === 'manning agent' || api.includes('manning');
  if (isCaptainLike) {
    return optionNameById(ctx.captainDropdownOptions, id);
  }
  if (isManningLike) {
    return optionNameById(ctx.manningDropdownOptions, id);
  }
  return (
    optionNameById(ctx.captainDropdownOptions, id) ||
    optionNameById(ctx.manningDropdownOptions, id)
  );
}

export interface MergeFormIntoVoyageContext {
  captainFactorCategory: string;
  editableRows: readonly FeatureMetadataRow[];
  factorFormKey: (row: FeatureMetadataRow) => string;
  factorUiKind: (row: FeatureMetadataRow) => ContributingFactorUiKind;
  slotCaptainKey: string;
  captainDropdownOptions: ReadonlyArray<{ id: unknown; name: unknown }>;
  manningDropdownOptions: ReadonlyArray<{ id: unknown; name: unknown }>;
}

function firstId(raw: unknown): string | null {
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }
  const s = String(raw[0] ?? '').trim();
  return s.length > 0 ? s : null;
}

/** Latest captain from the roster dropdown — not stale `slotCaptain` from first hydrate. */
function captainCrewSelectionIdFromSlot(
  slot: Record<string, unknown>,
  ctx: MergeFormIntoVoyageContext,
): string | null {
  return (
    firstId(slot[CAPTAIN_CREW_RATIO_WEIGHTED_API_NAME]) ??
    firstId(slot[ctx.slotCaptainKey]) ??
    null
  );
}

/**
 * Resolves the `perCaptain` map key for a captain slot. After recalculation the map keys follow
 * the API (`perCaptain` / roster order), while `slotCaptain` may still hold voyage-roster ids —
 * prefer a key that exists on the factor row, then slot order, then form fallback.
 */
/** Resolves `perCaptain` map key for a captain slot (exported for inference `onlyCaptainId`). */
export function resolvePerCaptainKeyForSlot(
  row: VoyageContributingFactorRow | null,
  roster: readonly CaptainListItem[],
  slotIndex: number,
  capFromForm: string | null,
): string {
  const rosterId = roster[slotIndex]?.captainId;
  const rosterKey = rosterId != null ? String(rosterId).trim() : '';
  const perCaptain = row?.perCaptain;
  if (perCaptain && typeof perCaptain === 'object') {
    if (rosterKey && Object.prototype.hasOwnProperty.call(perCaptain, rosterKey)) {
      return rosterKey;
    }
    const keys = Object.keys(perCaptain);
    const positional = keys[slotIndex];
    if (positional) {
      return positional;
    }
    if (capFromForm && Object.prototype.hasOwnProperty.call(perCaptain, capFromForm)) {
      return capFromForm;
    }
    // No-captain-assigned voyage: perCaptain is empty and roster is empty but the user has
    // made a selection. Use the zero-based slot index as the map key so the inference API
    // receives changedValue: {"0": captainDimCrewId} — the format it expects for a brand-new
    // captain assignment.
    if (!rosterKey && keys.length === 0 && capFromForm != null) {
      return String(slotIndex);
    }
  }
  return rosterKey || capFromForm || '';
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function applyScalarFromSliderOrTime(
  row: VoyageContributingFactorRow,
  ui: number,
  meta: FeatureMetadataRow,
): void {
  const model = formSliderToModelValue(ui, row, meta);
  row.aggregateValue = model;
  row.scalarValueUi =
    factorSliderUsesDecimalUi(meta) ||
    (typeof model === 'number' && model > 0 && model < 1)
      ? String(model)
      : String(ui);
}

function applyRootFactor(
  voyage: VoyageContributingFactorsVoyage,
  meta: FeatureMetadataRow,
  rawVal: unknown,
  ctx: MergeFormIntoVoyageContext,
): void {
  const row = findMutableFactorRow(voyage, meta.featureId);
  if (!row) {
    return;
  }
  const kind = ctx.factorUiKind(meta);
  if (kind === 'slider' || kind === 'time') {
    const ui = typeof rawVal === 'number' ? rawVal : Number(rawVal);
    if (!Number.isFinite(ui)) {
      return;
    }
    applyScalarFromSliderOrTime(row, ui, meta);
    return;
  }
  if (kind === 'bool') {
    row.aggregateValue = rawVal ? 1 : 0;
    row.scalarValueUi = rawVal ? '1' : '0';
    return;
  }
  if (kind === 'dropdown') {
    const ids = Array.isArray(rawVal) ? rawVal : [];
    const id = String(ids[0] ?? '');
    const label = resolveCaptainManningDropdownName(meta, id, ctx);
    row.scalarValueUi = label;
    return;
  }
  row.scalarValueUi = String(rawVal ?? '');
  const n = Number(rawVal);
  if (Number.isFinite(n)) {
    row.aggregateValue = n;
  }
}

function applyCaptainSlotFactor(
  voyage: VoyageContributingFactorsVoyage,
  meta: FeatureMetadataRow,
  slot: Record<string, unknown>,
  formKey: string,
  captainId: string,
  ctx: MergeFormIntoVoyageContext,
): void {
  const row = findMutableFactorRow(voyage, meta.featureId);
  if (!row || !captainId) {
    return;
  }
  const kind = ctx.factorUiKind(meta);
  const rawVal = slot[formKey];


  if (row.shape === 'perCaptainNamed' && kind === 'dropdown') {
    const ids = Array.isArray(rawVal) ? rawVal : [];
    const id = String(ids[0] ?? '').trim();
    const name = id ? resolveCaptainManningDropdownName(meta, id, ctx) : '';
    // For NO_CAPTAIN_ASSIGNED voyages perCaptain starts as {} — seed the slot so the
    // entry exists and changedFactors can detect the selection as a diff vs the empty reference.
    if (row.perCaptain && !Object.prototype.hasOwnProperty.call(row.perCaptain, captainId)) {
      (row.perCaptain as Record<string, VoyageContributingPerCaptainNamed>)[captainId] = {
        name: '',
        value: 0,
        shap: 0,
      };
    }
    const entry = row.perCaptain?.[captainId] as VoyageContributingPerCaptainNamed | undefined;
    if (entry && typeof entry === 'object') {
      entry.name = name;
      const crewNum = toFiniteNumber(id);
      entry.uiSelectedDimCrewId = crewNum;
    }
    return;
  }

  if (kind === 'slider' || kind === 'time') {
    const ui = typeof rawVal === 'number' ? rawVal : Number(rawVal);
    if (!Number.isFinite(ui)) {
      return;
    }
    const model = formSliderToModelValue(ui, row, meta);
    // Seed missing perCaptain slot (no-captain voyage) so the update can be applied.
    if (row.perCaptain && !Object.prototype.hasOwnProperty.call(row.perCaptain, captainId)) {
      (row.perCaptain as Record<string, VoyageContributingPerCaptainScalar>)[captainId] = {
        value: 0,
        shap: 0,
      };
    }
    const pc = row.perCaptain?.[captainId];
    if (pc && typeof pc === 'object' && 'value' in pc) {
      (pc as { value: number }).value = model;
    }
    return;
  }

  if (kind === 'bool') {
    // Seed missing perCaptain slot (no-captain voyage).
    if (row.perCaptain && !Object.prototype.hasOwnProperty.call(row.perCaptain, captainId)) {
      (row.perCaptain as Record<string, VoyageContributingPerCaptainScalar>)[captainId] = {
        value: 0,
        shap: 0,
      };
    }
    const pc = row.perCaptain?.[captainId];
    if (pc && typeof pc === 'object' && 'value' in pc) {
      (pc as { value: number }).value = rawVal ? 1 : 0;
    }
    return;
  }
}

function applyFormRawToVoyage(
  voyage: VoyageContributingFactorsVoyage,
  formRaw: Record<string, unknown>,
  roster: readonly CaptainListItem[],
  ctx: MergeFormIntoVoyageContext,
): void {
 
  const captainsForm = formRaw['captains'];
  for (const meta of ctx.editableRows) {
    const key = ctx.factorFormKey(meta);
    const isCaptain = meta.category.trim() === ctx.captainFactorCategory;

    if (isCaptain) {
      if (!Array.isArray(captainsForm)) {
        continue;
      }
      for (let si = 0; si < captainsForm.length; si++) {
        const slot = captainsForm[si] as Record<string, unknown> | undefined;
        if (!slot) {
          continue;
        }
        const capFromForm = captainCrewSelectionIdFromSlot(slot, ctx);
        const rowForKey = findMutableFactorRow(voyage, meta.featureId);
        const capId = resolvePerCaptainKeyForSlot(rowForKey, roster, si, capFromForm);
        if (!capId) {
          continue;
        }
        applyCaptainSlotFactor(voyage, meta, slot, key, String(capId), ctx);
      }
    } else {
      if (!(key in formRaw)) {
        continue;
      }
      applyRootFactor(voyage, meta, formRaw[key], ctx);
    }
  }

}

/**
 * Clones each voyage from GET contributing-factors and overlays current adjustable-factor `FormGroup` values
 * so an Inference API payload reflects edits (sliders, time, bool, captain dropdowns).
 */
export function mergeContributingFactorsApiDataWithForm(
  apiData: VoyageContributingFactorsApiData,
  formRootRaw: { voyages?: unknown[] },
  voyageIdsInOrder: readonly (string | number)[],
  ctx: MergeFormIntoVoyageContext,
): VoyageContributingFactorsVoyage[] {
  const formArr = Array.isArray(formRootRaw.voyages) ? formRootRaw.voyages : [];
  const out: VoyageContributingFactorsVoyage[] = [];

  for (const id of voyageIdsInOrder)
  {
    const vid = id;
    const base = contributingFactorsVoyageById(apiData, vid);
    if (!base) {
      continue;
    }
    const clone = structuredClone(base) as VoyageContributingFactorsVoyage;
    const formRaw = formArr.find((v: any) => v.voyageId === id);
    const roster = contributingFactorsCaptainRosterForSlots(clone);
    applyFormRawToVoyage(clone, formRaw as Record<string, unknown>, roster, ctx);
    out.push(clone);
  }

  return out;
}
